
import { prefixer } from 'stylis';
import { useState, useEffect, createContext } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import rtlPlugin from 'stylis-plugin-rtl';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@emotion/react';
import WEGLOT_CONFIG from '../config/weglotApiKey';

/* Context for unaffected components to be changed. */
export const DirectionContext = createContext('ltr');


//List of RTL language codes
const WEGLOT_RTL_LANGUAGES = ['he','ar','fa','ur'];


//For Speedy configuration, see https://github.com/emotion-js/emotion/discussions/2903
//the documentation does not outline this property.
const ltrCache = createCache({
    key:'muiltr',
    stylisPlugins:[],
    speedy: true,
});
    

const rtlCache = createCache({
    key:'muirtl',
    stylisPlugins:[prefixer, rtlPlugin],
    speedy: true,
});


const rtlTheme = createTheme({direction: 'rtl'});
const ltrTheme = createTheme({direction:'ltr'});

/**
 * @description - When an RTL language is enabled, the provider will modify MUI config as well as document 
 * layout to enable both RTL and LTR features.
 * @link https://mui.com/material-ui/guides/right-to-left/ - See MUI guide instructions for incorporating RTL.
 * @link see sample references: https://stackoverflow.com/questions/73297632/how-to-add-dynamic-rtl-support-for-next-js-material-ui-emotion
 * @param {React.ReactNode} children - Component that provider will wrap.
 * @returns - A wrapper component for App.js to provide RTL and LTR features for every component.
 */
export default function DirectionProvider({children}){

    const [directionLayout, setDirectionLayout] = useState('ltr');

    useEffect(()=> {
        window.Weglot.initialize({
            api_key: WEGLOT_CONFIG.key,
            hide_switcher: true 
        }); 

        /* Listen to when language is changed */
        window.Weglot.on('languageChanged', (newLang, _)=> {
            if(WEGLOT_RTL_LANGUAGES.indexOf(newLang) === -1){
                document.dir = 'ltr';
                setDirectionLayout('ltr');
            }
            else{
                document.dir='rtl';
                setDirectionLayout('rtl');
            }
        });
      
        /* Cleanup */
        return ()=> {
            window.Weglot.off('languageChanged');
        }
    },[]);


    return (
        <DirectionContext.Provider value={directionLayout}>
            <CacheProvider value={directionLayout === 'rtl'?rtlCache:ltrCache}>
                <ThemeProvider theme={directionLayout === 'rtl'?rtlTheme:ltrTheme}>
                        {children}
                </ThemeProvider>
            </CacheProvider>
        </DirectionContext.Provider>
    );


}
